import FullscreenSpinner from '@/components/common/FullScreenSpinner';
import DnsCheckForm from '@/components/DnsCheckForm';
import DnsResults from '@/components/DnsResults';
import SEOHead from '@/components/SEOHead';
import useDnsCheck from '@/hooks/useDnsCheck';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Card, Col, Descriptions, Divider, Row, Tag, Typography, Upload } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

const { Dragger } = Upload;

const BimiVisualizer = () => {
  const { data, isLoading, submitCheck } = useDnsCheck();
  const router = useRouter();
  const [imageError, setImageError] = useState(false);
  const [uploadedLogo, setUploadedLogo] = useState('');

  // Handler to process logo uploads using FileReader
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    // Check if there's a domain query parameter and initiate a DNS check
    if (router.query.domain) {
      submitCheck(router.query.domain);
    }
  }, [router.query, submitCheck]);

  // New helper that extracts both the BIMI URL and VMC status.
  const extractBimiInfo = (bimiRecords) => {
    if (!bimiRecords || !Array.isArray(bimiRecords) || bimiRecords.length === 0) {
      return { bimiUrl: '' };
    }
    const bimiRegex = /v=BIMI1;\s*l=([^;]+)(?:;|$)/;
    for (const record of bimiRecords) {
      const joined = record.join('');
      const bimiMatch = joined.match(bimiRegex);
      if (bimiMatch) {
        return {
          bimiUrl: bimiMatch[1],
        };
      }
    }
    return { bimiUrl: '' };
  };

  // Extract BIMI URL using existing parser (still valid)
  const bimiUrl = data && data.records.BIMI ? extractBimiInfo(data.records.BIMI).bimiUrl : '';

  // Use backend's CORRECT certificate type data (not flawed regex)
  const isVMC = data?.certificateInfo?.certificateType === 'VMC';

  // Determine which logo to use: detected BIMI logo or uploaded logo
  const logoToDisplay = bimiUrl || uploadedLogo;

  const hasCertInfo = data && data.certificateInfo && Object.keys(data.certificateInfo).length > 0;

  // Helper function to display certificate value or fallback
  const displayCertValue = (value, fallback = 'Not Available') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return typeof value === 'string' ? value.replace(/\\/g, '') : value;
  };

  return (
    <div>
      <SEOHead />

      <section
        className="products section hero v4"
        style={{ paddingTop: '132px', paddingBottom: '0' }}
      >
        <div className="container-default w-container">
          <div className="z-index-1">
            <div className="mg-bottom-24px">
              <div className="inner-container center">
                <div className="inner-container _861px---tablet center">
                  <div className="text-center">
                    <div className="mg-bottom-24px">
                      <div className="text-100 bold color-accent-1 text-uppercase">
                        <p>SEE YOUR BIMI IN ACTION</p>
                      </div>
                    </div>
                    <h1 className="mg-bottom-16px">BIMI Visualizer</h1>
                    <div className="inner-container _745px center">
                      <p className="color-neutral-600 mg-bottom-0 paragraph-medium">
                        Enter your domain below to preview how your BIMI logo will appear on leading
                        email clients. See your logo in action on Gmail with a Verified Mark
                        Certificate, complete with an exclusive blue checkmark that boosts brand
                        trust and authenticity.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="certificate_request_app" className="bg-neutral-100" style={{ paddingTop: '16px' }}>
        <div className="container-default w-container">
          <div className="mg-bottom-16px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <DnsCheckForm onSubmit={submitCheck} isLoading={isLoading} />
                    <FullscreenSpinner isLoading={isLoading} tip="Checking DNS records..." />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          <div className="mg-bottom-20px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    {data && (
                      <h2>
                        Results for{' '}
                        <Link href={`https://${data.domain}`} target="_blank">
                          {data.domain}
                        </Link>
                      </h2>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          {data && !logoToDisplay && (
            <div
              style={{
                textAlign: 'center',
                maxWidth: '500px',
                margin: '16px auto 64px',
              }}
            >
              <Dragger
                name="logo"
                multiple={false}
                accept="image/*"
                beforeUpload={(file) => {
                  // Mimic file input event for handleLogoUpload
                  handleLogoUpload({ target: { files: [file] } });
                  return false;
                }}
                showUploadList={false}
                style={{
                  padding: '16px',
                  borderWidth: '2px',
                  borderRadius: '16px',
                  backgroundColor: '#fafafa',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <UploadOutlined
                    style={{ fontSize: '48px', marginBottom: '16px', color: '#247bfe' }}
                  />
                  <p>Click or drag your logo file here to upload</p>
                </div>
              </Dragger>
            </div>
          )}
          {data && logoToDisplay && (
            <>
              <div className="desktop-visualizer mg-bottom-80px">
                <div className="inner-container">
                  <Row gutter={[24, 24]}>
                    <Col xs={24}>
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: 1215,
                          margin: '0 auto',
                          fontSize: 'clamp(12px, 1.2vw, 16px)',
                        }}
                      >
                        {/* Desktop Background Image */}
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '56.54%',
                            backgroundImage: 'url(/images/bg-bimi-visualizer.png)',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                          }}
                        >
                          {/* Desktop Overlays */}
                          <>
                            <div
                              style={{
                                position: 'absolute',
                                left: '28%',
                                top: '39.8%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6em',
                              }}
                              data-overlay="desktop"
                            >
                              <img
                                src={logoToDisplay}
                                alt="BIMI Logo"
                                style={{
                                  width: 'clamp(20px, 5vw, 40px)',
                                  height: 'clamp(20px, 5vw, 40px)',
                                  borderRadius: '50%',
                                  objectFit: 'contain',
                                  background: 'white',
                                  padding: '2px',
                                }}
                              />
                              <span
                                style={{
                                  whiteSpace: 'nowrap',
                                  color: 'rgba(0,0,0,0.6)',
                                  fontWeight: 500,
                                  textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                  fontSize: '1.3em',
                                }}
                              >
                                {data.domain.split('.')[0].charAt(0).toUpperCase() +
                                  data.domain.split('.')[0].slice(1).toLowerCase()}
                                {isVMC && (
                                  <img
                                    src="/images/icon-blue-check-glow.svg"
                                    alt="VMC issued"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      position: 'relative',
                                      top: '-5px',
                                      marginLeft: '3px',
                                    }}
                                  />
                                )}
                              </span>
                            </div>
                            <div
                              style={{
                                position: 'absolute',
                                left: '71.5%',
                                top: '32.6%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1.1em',
                              }}
                              data-overlay="mobile"
                            >
                              <img
                                src={logoToDisplay}
                                alt="BIMI Logo"
                                style={{
                                  width: 'clamp(32px, 5vw, 62px)',
                                  height: 'clamp(32px, 5vw, 62px)',
                                  borderRadius: '50%',
                                  objectFit: 'contain',
                                  background: 'white',
                                  padding: '2px',
                                }}
                              />
                              <span
                                style={{
                                  whiteSpace: 'nowrap',
                                  color: 'rgba(0,0,0,0.6)',
                                  fontWeight: 500,
                                  textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                  fontSize: '1.4em',
                                }}
                              >
                                {data.domain.split('.')[0].charAt(0).toUpperCase() +
                                  data.domain.split('.')[0].slice(1).toLowerCase()}
                                {isVMC && (
                                  <img
                                    src="/images/icon-blue-check-glow.svg"
                                    alt="VMC issued"
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      position: 'relative',
                                      top: '-5px',
                                      marginLeft: '3px',
                                    }}
                                  />
                                )}
                              </span>
                            </div>
                          </>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="mobile-visualizer mg-bottom-80px">
                <div className="inner-container">
                  <Row gutter={[24, 24]}>
                    <Col xs={24}>
                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: 800,
                          margin: '0 auto 24px',
                          fontSize: 'clamp(12px, 1.2vw, 16px)',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '56.54%',
                            backgroundImage: 'url(/images/bg-bimi-visualizer-mac.png)',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: '33%',
                              top: '27%',
                              transform: 'translateY(-50%)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5em',
                            }}
                            data-overlay="laptop"
                          >
                            <img
                              src={logoToDisplay}
                              alt="BIMI Logo"
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                objectFit: 'contain',
                                background: 'white',
                                padding: '2px',
                              }}
                            />
                            <span
                              style={{
                                whiteSpace: 'nowrap',
                                color: 'rgba(0,0,0,0.6)',
                                fontWeight: 500,
                                textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                fontSize: '1.4em',
                              }}
                            >
                              {data.domain.split('.')[0].charAt(0).toUpperCase() +
                                data.domain.split('.')[0].slice(1).toLowerCase()}
                              {isVMC && (
                                <img
                                  src="/images/icon-blue-check-glow.svg"
                                  alt="VMC issued"
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    position: 'relative',
                                    top: '-5px',
                                    marginLeft: '3px',
                                  }}
                                />
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          position: 'relative',
                          width: '100%',
                          maxWidth: 375,
                          margin: '0 auto',
                          fontSize: 'clamp(12px, 1.2vw, 16px)',
                        }}
                      >
                        <div
                          style={{
                            position: 'relative',
                            width: '100%',
                            paddingTop: '177%',
                            backgroundImage: 'url(/images/bg-bimi-visualizer-iphone.png)',
                            backgroundSize: 'contain',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                          }}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              left: '13%',
                              top: '23.5%',
                              transform: 'translateY(-50%)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '1.1em',
                            }}
                            data-overlay="iphone"
                          >
                            <img
                              src={logoToDisplay}
                              alt="BIMI Logo"
                              style={{
                                width: 'clamp(60px, 5vw, 60px)',
                                height: 'clamp(60px, 5vw, 60px)',
                                borderRadius: '50%',
                                objectFit: 'contain',
                                background: 'white',
                                padding: '0',
                              }}
                            />
                            <span
                              style={{
                                whiteSpace: 'nowrap',
                                color: 'rgba(0,0,0,0.6)',
                                fontWeight: 500,
                                textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                                fontSize: '1.5em',
                              }}
                            >
                              {data.domain.split('.')[0].charAt(0).toUpperCase() +
                                data.domain.split('.')[0].slice(1).toLowerCase()}
                              {isVMC && (
                                <img
                                  src="/images/icon-blue-check-glow.svg"
                                  alt="VMC issued"
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    position: 'relative',
                                    top: '-5px',
                                    marginLeft: '3px',
                                  }}
                                />
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              {data && logoToDisplay && !bimiUrl && (
                <div
                  style={{
                    textAlign: 'center',
                    maxWidth: '500px',
                    margin: '16px auto 64px',
                  }}
                >
                  <Dragger
                    name="reuploadLogo"
                    multiple={false}
                    accept="image/*"
                    beforeUpload={(file) => {
                      handleLogoUpload({ target: { files: [file] } });
                      return false;
                    }}
                    showUploadList={false}
                    style={{
                      padding: '16px',
                      borderWidth: '2px',
                      borderRadius: '16px',
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <UploadOutlined
                        style={{ fontSize: '48px', marginBottom: '16px', color: '#247bfe' }}
                      />
                      <p>
                        Previewing your logo if BIMI were set up. <br />
                        Click or drag a file here to upload a different logo.
                      </p>
                    </div>
                  </Dragger>
                </div>
              )}
            </>
          )}
          <div className="mg-bottom-80px">
            <div className="inner-container">
              <Row gutter={[24, 24]}>
                <Col xs={24} md={16}>
                  {data && <DnsResults data={data} />}
                </Col>

                <Col xs={24} md={8}>
                  {data && (
                    <>
                      <Card
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          marginBottom: '20px',
                        }}
                      >
                        <h3>
                          BIMI Logo:
                          {data &&
                            (logoToDisplay ? (
                              <CheckCircleOutlined style={{ color: 'green', marginLeft: '10px' }} />
                            ) : (
                              <CloseCircleOutlined style={{ color: 'red', marginLeft: '10px' }} />
                            ))}
                        </h3>
                        {data &&
                          (logoToDisplay ? (
                            <div>
                              {!imageError ? (
                                <img
                                  src={logoToDisplay}
                                  alt="BIMI Logo"
                                  style={{ maxWidth: '100%', height: 'auto' }}
                                  onError={() => setImageError(true)}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    textAlign: 'center',
                                    lineHeight: '24px',
                                  }}
                                >
                                  <WarningOutlined
                                    style={{ color: '#d4a017', fontSize: '164px' }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              <div
                                style={{ width: '100%', textAlign: 'center', lineHeight: '24px' }}
                              >
                                <WarningOutlined style={{ color: '#d4a017', fontSize: '164px' }} />
                              </div>

                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  height: '100%',
                                }}
                              >
                                <Typography.Text>No BIMI logo found!</Typography.Text>
                              </div>

                              <div
                                className="buttons-row mg-top-16px"
                                style={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Link
                                  href="/products/digicert-verified-mark-certificate"
                                  className="btn-primary w-button width-100"
                                >
                                  Get BIMI Compliant Now
                                  <span className="line-rounded-icon link-icon-right"></span>
                                </Link>
                              </div>
                            </>
                          ))}
                      </Card>

                      {hasCertInfo && (
                        <Card
                          title={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <SafetyCertificateOutlined
                                style={{ fontSize: '18px', marginRight: '8px', color: '#52c41a' }}
                              />
                              <span>
                                {data.certificateInfo.certificateType === 'CMC' ||
                                (data.certificateInfo.subject &&
                                  data.certificateInfo.subject.includes('Prior Use Mark'))
                                  ? 'CMC'
                                  : 'VMC'}{' '}
                                Certificate Details
                              </span>
                            </div>
                          }
                          style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          }}
                        >
                          <Descriptions
                            bordered
                            size="small"
                            column={1}
                            labelStyle={{ fontWeight: 'bold' }}
                          >
                            <Descriptions.Item label="Organization">
                              {displayCertValue(data.certificateInfo.organization)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Certificate Type">
                              {data.certificateInfo.subject &&
                              data.certificateInfo.subject.includes('Prior Use Mark')
                                ? 'CMC'
                                : displayCertValue(data.certificateInfo.certificateType, 'VMC')}
                            </Descriptions.Item>

                            {data.certificateInfo.markType && (
                              <Descriptions.Item label="Mark Type">
                                {displayCertValue(data.certificateInfo.markType)}
                              </Descriptions.Item>
                            )}

                            <Descriptions.Item label="Valid From">
                              {displayCertValue(data.certificateInfo.validFrom)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Valid To">
                              {displayCertValue(data.certificateInfo.validTo)}
                            </Descriptions.Item>

                            <Descriptions.Item label="Days Remaining">
                              {data.certificateInfo.daysLeft !== undefined ? (
                                <Tag color={data.certificateInfo.daysLeft > 30 ? 'green' : 'red'}>
                                  {data.certificateInfo.daysLeft} days
                                </Tag>
                              ) : (
                                'Not Available'
                              )}
                            </Descriptions.Item>

                            <Descriptions.Item label="Issuer">
                              {displayCertValue(
                                data.certificateInfo.caOrganization || data.certificateInfo.issuer
                              )}
                            </Descriptions.Item>

                            {data.certificateInfo.trademarkRegistration && (
                              <Descriptions.Item label="Trademark Registration">
                                {data.certificateInfo.trademarkRegistration}
                              </Descriptions.Item>
                            )}

                            {data.certificateInfo.serialNumber && (
                              <Descriptions.Item label="Serial Number">
                                {data.certificateInfo.serialNumber}
                              </Descriptions.Item>
                            )}
                          </Descriptions>

                          <Divider />

                          <div>
                            <a
                              href={data.certificateInfo.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-primary w-button width-100"
                              style={{ textAlign: 'center', display: 'block' }}
                            >
                              View Full Certificate
                            </a>
                          </div>
                        </Card>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .bimi-visualizer-container {
          position: relative;
          max-width: 1215px;
          margin: 40px auto;
        }

        .bimi-overlay {
          position: absolute;
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .bimi-logo-circle {
          border-radius: 50%;
          object-fit: contain;
          background: white;
          padding: 2px;
        }

        .domain-text {
          color: white;
          font-weight: 700;
          text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
          font-size: 1.2em;
        }

        @media (max-width: 768px) {
          .desktop-visualizer {
            display: none;
          }
          .mobile-visualizer {
            display: block;
          }
        }

        @media (min-width: 769px) {
          .desktop-visualizer {
            display: block;
          }
          .mobile-visualizer {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default BimiVisualizer;
